import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { Material } from '../types';
import ConfirmDialog from './ConfirmDialog';
import MaterialDialog from './MaterialDialog';

const MaterialyPage: React.FC = () => {
  const [materialy, setMaterialy] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [materialToEdit, setMaterialToEdit] = useState<Material | null>(null);

  const truncateText = (text: string | undefined, maxLength: number = 100): string => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const fetchMaterialy = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getMaterials();
      setMaterialy(data);
    } catch (err: any) {
      setError('Chyba při načítání materiálů: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setMaterialToEdit(null);
    setEditDialogOpen(true);
  };

  const handleEdit = (material: Material) => {
    setMaterialToEdit(material);
    setEditDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    setMaterialToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleSaveMaterial = async (data: { nazev: any; popis: any }) => {
    try {
      setLoading(true);
      setError(null);
      
      if (materialToEdit) {
        // Editace existujícího materiálu
        await apiClient.updateMaterial(materialToEdit.id, data);
      } else {
        // Vytvoření nového materiálu
        await apiClient.createMaterial(data);
      }
      
      setEditDialogOpen(false);
      setMaterialToEdit(null);
      await fetchMaterialy();
    } catch (err: any) {
      setError('Chyba při ukládání materiálu: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (materialToDelete === null) return;

    try {
      setLoading(true);
      setError(null);
      await apiClient.deleteMaterial(materialToDelete);
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
      await fetchMaterialy();
    } catch (err: any) {
      // Zobrazení detailní chybové hlášky z API
      const errorMessage = err.response?.data?.message || err.message || 'Chyba při mazání materiálu';
      
      setError(errorMessage);
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setMaterialToDelete(null);
  };

  const cancelEdit = () => {
    setEditDialogOpen(false);
    setMaterialToEdit(null);
  };

  useEffect(() => {
    fetchMaterialy();
  }, []);

  if (loading) return <div style={styles.loading}>Načítání...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Materiály</h2>
        <button style={styles.addButton} onClick={handleAdd} title="Přidat nový materiál">
          ➕ Přidat materiál
        </button>
      </div>
      
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Název</th>
            <th style={styles.th}>Popis</th>
            <th style={styles.th}>Akce</th>
          </tr>
        </thead>
        <tbody>
          {materialy.length === 0 ? (
            <tr>
              <td colSpan={4} style={styles.noData}>Žádné materiály nenalezeny</td>
            </tr>
          ) : (
            materialy.map((material) => (
              <tr key={material.id}>
                <td style={styles.td}>{material.id}</td>
                <td style={styles.td}>{material.nazev}</td>
                <td style={styles.td}>{truncateText(material.popis)}</td>
                <td style={styles.td}>
                  <div style={styles.actions}>
                    <button
                      style={styles.editButton}
                      title="Editovat"
                      onClick={() => handleEdit(material)}
                    >
                      ✏️
                    </button>
                    <button
                      style={styles.deleteButton}
                      title="Smazat"
                      onClick={() => handleDelete(material.id)}
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="Potvrzení smazání"
        message="Opravdu chcete smazat tento materiál? Tato akce je nevratná."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Smazat"
        cancelText="Zrušit"
      />

      <MaterialDialog
        isOpen={editDialogOpen}
        material={materialToEdit}
        onSave={handleSaveMaterial}
        onCancel={cancelEdit}
      />
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    margin: 0,
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '20px',
    fontSize: '16px',
  },
  error: {
    padding: '20px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    margin: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '20px',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  th: {
    backgroundColor: '#343a40',
    color: 'white',
    padding: '12px',
    textAlign: 'left' as const,
    fontWeight: 'bold' as const,
    borderBottom: '2px solid #dee2e6',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
  },
  noData: {
    textAlign: 'center' as const,
    padding: '20px',
    color: '#6c757d',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'transform 0.2s',
  },
  deleteButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'transform 0.2s',
  },
};

export default MaterialyPage;
